// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
import { Button } from '@change/ds-core-ui/Button';
import { ModalHeader } from '@change/ds-core-ui/Modal';
import { ArrowLeftIcon } from '@change/ds-icons/ArrowLeftIcon';
import { cn } from '@change/ds-utils';

import type { I18nUtilities } from '@change-corgi/core-react/i18n';

// ModalHeader clones its leading slot with its own `size` and `className`, so the back button is styled
// through `leadingSlotClassName`, and its focus ring is spelled out (the injected size matches none of
// Button's own).
const BACK_BUTTON_CLASSNAME = cn(
	'p-0 no-underline',
	'focus-visible:outline-stroke-focus focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-solid',
);

/** EXPERIMENT: psf_promote_or_share_qr_code_experiment. The QR and poster views' header: back and close, no title. */
type StepHeader = { onBack: () => void; backButtonTestId: string };

/**
 * The header for the current view. A plain function, not a component, so the Modal still finds its
 * ModalHeader child. The QR and poster views' headers have the back button in the leading slot.
 */
export function renderHeader({
	shareVisual,
	stepHeader,
	translate,
}: {
	shareVisual: string | undefined;
	/** Set only on the QR and poster views. */
	stepHeader: StepHeader | null;
	translate: I18nUtilities['translate'];
}) {
	if (!stepHeader) {
		return (
			<ModalHeader
				title={shareVisual ? translate('corgi.psf.promote_or_share.share_slide_up.aria_label') : undefined}
			/>
		);
	}
	return (
		<ModalHeader
			leadingSlotClassName={BACK_BUTTON_CLASSNAME}
			leadingSlot={
				// iconOnly buttons render no children: the accessible name is the aria-label.
				<Button
					iconOnly
					variant="tertiary"
					leadingIcon={<ArrowLeftIcon size="medium" />}
					aria-label={translate('corgi.psf.promote_or_share.share_slide_up.qr_code.back_button')}
					onClick={stepHeader.onBack}
					data-testid={stepHeader.backButtonTestId}
				/>
			}
		/>
	);
}

/** EXPERIMENT: psf_promote_or_share_qr_code_experiment. The QR view backs to the grid, the poster view to the QR view. */
export function getStepHeader({
	isQrView,
	isPosterView,
	showGrid,
	showQrView,
}: {
	isQrView: boolean;
	isPosterView: boolean;
	showGrid: () => void;
	showQrView: () => void;
}): StepHeader | null {
	if (isQrView) return { onBack: showGrid, backButtonTestId: 'qr-code-view-back-button' };
	if (isPosterView) return { onBack: showQrView, backButtonTestId: 'poster-view-back-button' };
	return null;
}
